import type { Lang } from "./i18n";

export const SORTS = [
  { value: "newest", label: { en: "Newest", fr: "Plus récentes" } },
  { value: "popular", label: { en: "Most popular", fr: "Plus populaires" } },
  { value: "quickest", label: { en: "Quickest", fr: "Plus rapides" } },
  { value: "relevance", label: { en: "Best match", fr: "Pertinence" } },
] as const;

export type SortValue = (typeof SORTS)[number]["value"];

export const DIFFICULTIES = [
  { value: "EASY", label: { en: "Easy", fr: "Facile" } },
  { value: "MEDIUM", label: { en: "Medium", fr: "Moyen" } },
  { value: "HARD", label: { en: "Hard", fr: "Difficile" } },
] as const;

// Bilingual filter entries. The English slug is the canonical id stored in
// state/URL (stable across language switches); each entry carries the
// language-specific slug + name used for the API call and display.
export interface LocalizedTerm {
  id: string; // canonical = English slug
  en: { slug: string; name: string };
  fr: { slug: string; name: string };
}

const cat = (es: string, en: string, fs: string, fn: string): LocalizedTerm => ({
  id: es,
  en: { slug: es, name: en },
  fr: { slug: fs, name: fn },
});

export const CATEGORIES: LocalizedTerm[] = [
  cat("main-courses", "Main courses", "plats", "Plats"),
  cat("starters", "Starters", "entrees", "Entrées"),
  cat("appetizers", "Appetizers and starters", "apero", "Apéro et entrées"),
  cat("bowls-and-salads", "Bowls and salads", "bols-et-salades", "Bols et salades"),
  cat("meal-soups", "Meal soups", "soupes-repas", "Soupes repas"),
  cat("pasta", "Pasta", "pates", "Pâtes"),
  cat("tofu-legumes-and-seitan", "Tofu, legumes and seitan", "tofu-legumineuses-et-seitan", "Tofu, légumineuses et seitan"),
  cat("sandwiches-and-burgers", "Sandwiches and burgers", "sandwichs-et-burgers", "Sandwichs et burgers"),
  cat("pizzas-tarts-and-quiches", "Pizzas, tarts and quiches", "pizzas-tartes-et-quiches", "Pizzas, tartes et quiches"),
  cat("sides", "Sides", "accompagnements", "Accompagnements"),
  cat("to-share", "To share", "a-partager", "À partager"),
  cat("dips", "Dips", "trempettes", "Trempettes"),
  cat("bites", "Bites", "bouchees", "Bouchées"),
  cat("desserts", "Desserts", "desserts", "Desserts"),
  cat("pastries", "Pastries", "patisseries", "Pâtisseries"),
  cat("crepes-doughnuts-and-waffles", "Crepes, doughnuts, waffles", "crepes-beignets-et-gaufres", "Crêpes, beignets, gaufres"),
  cat("verrines", "Verrines", "verrines", "Verrines"),
  cat("healthy-snacks", "Healthy snacks", "snacks-collations-sante", "Snacks & collations santé"),
  cat("breakfasts-and-brunches", "Breakfasts and brunches", "petits-dejeuners-et-brunchs", "Petits déjeuners et brunchs"),
  cat("sweet-morning", "Sweet morning", "matin-sucre", "Matin sucré"),
];

export const TAGS: LocalizedTerm[] = [
  cat("gluten-free", "Gluten-free", "sans-gluten", "Sans gluten"),
  cat("oven-free", "Oven-free", "sans-four", "Sans four"),
  cat("blender-free", "Blender-free", "sans-blender", "Sans blender"),
  cat("low-budget", "Low budget", "petit-budget", "Petit budget"),
  cat("batch-cooking", "Batch cooking", "batch-cooking", "Batch cooking"),
  cat("lunchbox", "Lunchbox", "lunchbox", "Lunchbox"),
  cat("for-hosting", "For hosting", "pour-recevoir", "Pour recevoir"),
  cat("picnic", "Picnic", "pique-nique", "Pique-nique"),
  cat("barbecue", "Barbecue", "barbecue", "Barbecue"),
  cat("asian-cuisine", "Asian cuisine", "cuisine-asiatique", "Cuisine asiatique"),
  cat("spring-summer", "Spring / Summer", "printemps-ete", "Printemps / été"),
  cat("autumn-winter", "Autumn / Winter", "automne-hiver", "Automne / hiver"),
];

// Translate a canonical (English) slug to the language-specific API slug.
export function toApiSlug(list: LocalizedTerm[], id: string, lang: Lang): string {
  return list.find(e => e.id === id)?.[lang].slug ?? id;
}
